import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { api, extractError } from "../lib/api";
import { accountsPartyWeightStatus } from "../lib/accountsSettlement";
import { kgToQtl, qtlToKg } from "../lib/weightUnits";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";

/**
 * Accounts step 1 for truck-load: capture party weighbridge net when missing from weighbridge.
 */
const AccountsPartyWeightEditor = forwardRef(function AccountsPartyWeightEditor(
  { purchase, canEdit, onSaved, onDraftQtlChange },
  ref,
) {
  const status = accountsPartyWeightStatus(purchase);
  const [kg, setKg] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const qtl = purchase?.party_net_weight_qtl;
    if (qtl != null && Number(qtl) > 0) {
      const asKg = qtlToKg(qtl);
      setKg(asKg != null ? String(asKg) : "");
    } else {
      setKg("");
    }
  }, [purchase?.id, purchase?.party_net_weight_qtl]);

  useEffect(() => {
    if (!onDraftQtlChange) return undefined;
    const qtl = kgToQtl(kg);
    onDraftQtlChange(qtl != null && qtl > 0 ? qtl : null);
  }, [kg, onDraftQtlChange]);

  const savedQtl = purchase?.party_net_weight_qtl;
  const hasSaved = savedQtl != null && Number(savedQtl) > 0;

  const save = async () => {
    if (!status.required) return true;
    const partyNetQtl = kgToQtl(kg);
    if (hasSaved && partyNetQtl != null && Math.abs(Number(savedQtl) - partyNetQtl) < 0.001) {
      return true;
    }
    if (partyNetQtl == null || partyNetQtl <= 0) {
      toast.error("Enter party side weight in kg (from party weighbridge slip)");
      return false;
    }
    try {
      setSaving(true);
      await api.patch(`/purchases/${purchase.id}/party-weight`, {
        party_net_weight_qtl: partyNetQtl,
      });
      toast.success("Party side weight saved");
      await onSaved?.();
      return true;
    } catch (err) {
      toast.error(extractError(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ save }), [save]);

  if (!status.required) return null;

  return (
    <div
      className={`rounded-md border space-y-3 p-3 sm:p-4 min-w-0 ${
        hasSaved ? "border-teal-200 bg-teal-50/40" : "border-amber-300 bg-amber-50/70"
      }`}
      data-testid="accounts-party-weight-editor"
    >
      <div className="min-w-0">
        <p className="text-xs font-medium text-amber-950">
          {hasSaved ? "Party side weight" : "Step 1 — Party side weight required"}
        </p>
        <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed break-words">
          {hasSaved
            ? "Recorded from party weighbridge. Use Next to continue to contract linkage."
            : status.message}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-end gap-3 min-w-0">
        <div className="flex-1 min-w-0 max-w-full sm:max-w-[220px]">
          <Label className="text-[10px] text-muted-foreground">Party net (kg)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            className="h-9 mt-0.5 w-full"
            value={kg}
            onChange={(e) => setKg(e.target.value)}
            disabled={!canEdit || saving}
            placeholder="e.g. 30500"
            data-testid="accounts-party-weight-kg"
          />
          {kg !== "" && kgToQtl(kg) != null && (
            <p className="text-[11px] text-muted-foreground mt-1 tabular">
              = {kgToQtl(kg).toFixed(2)} qtl
              {!hasSaved && " · use Next to save and continue"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
});

export default AccountsPartyWeightEditor;
